import { faBagShopping, faUser } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { usePermissions } from '@shared/hooks/usePermissions';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pack } from '../../models/Pack';
import { DropdownActions } from '../interaction';

interface PackCardProps {
  pack: Pack;
  onClick?: (pack: Pack) => void;
  onView?: (pack: Pack) => void;
  onEdit?: (pack: Pack) => void;
  onLink?: (pack: Pack) => void;
  onDelete?: (pack: Pack) => void;
  className?: string;
}

const PackCard = React.memo<PackCardProps>(({ pack, onClick, onView, onEdit, onLink, onDelete, className = '' }) => {
  const { t } = useTranslation('packsModule');
  const { canAccess } = usePermissions();

  // 🎯 Verificar si el usuario es tenant_3pl
  const isTenant3pl = canAccess(['tenant_3pl']);

  // 🎯 Calcular total de productos de la última versión únicamente
  const latestVersion = pack.versions[pack.versions.length - 1];
  const totalProducts = latestVersion?.lines.reduce((total, line) => total + line.quantity, 0) || 0;

  // 🎯 Estado para controlar si el título está expandido
  const [isTitleExpanded, setIsTitleExpanded] = useState(false);

  // 🎯 Handler optimizado con useCallback para evitar re-renders
  const handleTitleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation(); // Evitar que se active el onClick del card
    setIsTitleExpanded((prev) => !prev);
  }, []);

  // 🎯 Handler optimizado para el click del card
  const handleCardClick = useCallback(() => {
    onClick?.(pack);
  }, [onClick, pack]);

  return (
    <div
      className={`
                dark:bg-card-bg border border-card-border rounded-xl p-4 bg-body-bg
                transition-colors
                ${className}
            `}
      onClick={handleCardClick}
      data-testid={`pack-card-${pack.id}`}
    >
      {/* Header del pack */}
      <div className="flex justify-between items-start">
        <div className="flex-1 min-w-0">
          {' '}
          {/* min-w-0 para permitir truncate */}
          <p
            className={`font-medium text-foreground cursor-pointer transition-colors ${
              isTitleExpanded ? '' : 'truncate'
            }`}
            title={isTitleExpanded ? undefined : pack.title}
            onClick={handleTitleClick}
            data-testid={`pack-title-${pack.id}`}
          >
            {pack.title}
          </p>
        </div>
        {/* Dropdown Actions */}
        <div className="ml-2 flex-shrink-0">
          <DropdownActions pack={pack} onView={onView} onEdit={onEdit} onLink={onLink} onDelete={onDelete} />
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center text-sm">
          <FontAwesomeIcon icon={faBagShopping} className="mr-2" size="lg" />

          <span className="text-complementary-text">{t('table.products')}</span>
          <span className="font-medium ml-2 bg-card-border px-3 py-1 rounded-full">
            {totalProducts > 0 ? totalProducts : t('card.no_products')}
          </span>
        </div>

        {/* Mostrar team si es tenant_3pl */}
        {isTenant3pl && pack.team?.name && (
          <div className="flex items-center text-sm pt-1">
            <FontAwesomeIcon icon={faUser} className="mr-2" size="lg" />
            <span className="text-complementary-text text-xs">{pack.team.name}</span>
          </div>
        )}
      </div>
    </div>
  );
});

export default PackCard;
